import React, { Component, type ReactNode } from 'react';
import type { Store } from 'redux';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { Provider as ReduxProvider } from '@atlassian/jira-react-redux/src/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';

type Props = {
	children: ReactNode;
	/**
	 * ID for error reporting.
	 * @see {JSErrorBoundary}
	 */
	id?: string;
	/**
	 * Team name for error reporting.
	 * @see {JSErrorBoundary}
	 */
	teamName?: string;
	/**
	 * Package name for error reporting.
	 * @see {JSErrorBoundary}
	 */
	packageName?: string;
	/**
	 * Fallback behaviour when error encountered. Default: 'flag'.
	 * @see {JSErrorBoundary}
	 */
	fallback?: 'flag' | 'page';
	/**
	 * Redux store to add a provider for. Default: none.
	 * @see {Store}
	 * @see {ReduxProvider}
	 */
	store?: Store<unknown>;
	/**
	 * Whether an error boundary should wrap the provided children. Default: true.
	 * @see {JSErrorBoundary}
	 * @deprecated do not use AppBase if you don't want an error boundary!
	 */
	hasErrorBoundary?: boolean;
};

/**
 * Acts as a foundational layer for applications, dynamically integrating either an error boundary or a Redux provider based on props. This component ensures that child components have access to application state via Redux and robust error handling through conditional error boundaries. It enhances application stability and state management practices by providing these critical infrastructural features.
 */
// eslint-disable-next-line jira/react/no-class-components
class AppBaseCC extends Component<Props> {
	static defaultProps = {
		id: undefined,
		store: undefined,
		hasErrorBoundary: true,
	};

	renderChildrenWithStore() {
		if (!this.props.store) {
			return this.props.children;
		}
		return <ReduxProvider store={this.props.store}>{this.props.children}</ReduxProvider>;
	}

	render() {
		if (this.props.hasErrorBoundary) {
			return (
				<JSErrorBoundary
					id={this.props.id ?? '@atlassian/jira-app-base::unknown-app'}
					teamName={this.props.teamName}
					// JSErrorBoundary has page as the default, so we opt-out here for now
					fallback={this.props.fallback ?? 'flag'}
					// TODO: packageName should be required. Every call-site
					// should be updated to pass it.
					packageName={this.props.packageName ?? '@atlassian/jira-app-base'}
				>
					{this.renderChildrenWithStore()}
				</JSErrorBoundary>
			);
		}
		return this.renderChildrenWithStore();
	}
}

/**
 * A boundary around an "app," where an app is a distinct piece of functionality, and could potentially be nested within another app.
 *
 * Wraps children in an error boundary (by default) and a redux store provider (if store is passed).
 */
function AppBaseFC({
	// FIXME: id should be required
	id = '@atlassian/jira-app-base::unknown-app',
	// FIXME: packageName should be required
	packageName = '@atlassian/jira-app-base',
	teamName,
	hasErrorBoundary = true,
	// JSErrorBoundary fallback default is 'page' but AppBase is used around any "app"
	fallback = 'flag',
	store,
	children,
}: Props) {
	let contents: ReactNode = children;

	if (store) {
		contents = <ReduxProvider store={store}>{contents}</ReduxProvider>;
	}

	if (hasErrorBoundary) {
		contents = (
			<JSErrorBoundary id={id} teamName={teamName} fallback={fallback} packageName={packageName}>
				{contents}
			</JSErrorBoundary>
		);
	}

	return contents;
}

export default componentWithFG('magma-app-base-function-component', AppBaseFC, AppBaseCC);
